import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react';

const FOCUSABLE = [
  'a[href]',
  'area[href]',
  'button:not([disabled])',
  'input:not([disabled]):not([type="hidden"])',
  'select:not([disabled])',
  'textarea:not([disabled])',
  'iframe',
  '[tabindex]:not([tabindex="-1"])',
  '[contenteditable="true"]',
].join(',');

/** The keyboard-focusable elements inside `root`, in DOM (tab) order. */
export function focusableWithin(root: HTMLElement): HTMLElement[] {
  return Array.from(root.querySelectorAll<HTMLElement>(FOCUSABLE)).filter(
    (el) => !el.hasAttribute('inert') && el.getAttribute('aria-hidden') !== 'true',
  );
}

// Open focus traps, oldest first: only the topmost (a preview opened over its
// detail dialog) owns Tab, so stacked dialogs never fight over focus.
const trapStack: symbol[] = [];

export interface DialogFocusOptions {
  /** Element to focus on open. Default: the first focusable control inside the
   * container, else the container itself (give it tabIndex={-1}). */
  initialFocusRef?: RefObject<HTMLElement | null>;
  /** Off while the dialog is not mounted/open. Default true. */
  enabled?: boolean;
}

/**
 * The modal-dialog focus contract (WAI-ARIA APG), shared by every dialog, sheet
 * and the mobile nav drawer:
 *   - on open, focus moves INTO the dialog (the initial-focus element, else its
 *     first control, else the container) — once, not on every re-render, so a
 *     polling query never yanks focus out of a field being typed in;
 *   - Tab / Shift+Tab cycle within the dialog (focus never leaks to the page
 *     behind, which the old dialogs allowed);
 *   - on close, focus RETURNS to the element that had it when the dialog opened
 *     (the button that opened it), if that element is still in the document.
 */
export function useDialogFocus(
  containerRef: RefObject<HTMLElement | null>,
  { initialFocusRef, enabled = true }: DialogFocusOptions = {},
): void {
  const initialRef = useRef(initialFocusRef);
  initialRef.current = initialFocusRef;

  // Capture the opener synchronously at mount (before any child effect moves
  // focus), then hand it back on unmount.
  useLayoutEffect(() => {
    if (!enabled) return;
    const opener = document.activeElement instanceof HTMLElement ? document.activeElement : null;
    const container = containerRef.current;
    if (container) {
      const target =
        initialRef.current?.current ?? focusableWithin(container)[0] ?? container;
      target.focus();
    }
    return () => {
      if (opener && opener.isConnected) opener.focus();
    };
    // Mount/unmount only: re-running would re-steal focus on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled]);

  useEffect(() => {
    if (!enabled) return;
    const id = Symbol('focus-trap');
    trapStack.push(id);
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'Tab' || trapStack[trapStack.length - 1] !== id) return;
      const container = containerRef.current;
      if (!container) return;
      const items = focusableWithin(container);
      if (items.length === 0) {
        e.preventDefault();
        container.focus();
        return;
      }
      const first = items[0]!;
      const last = items[items.length - 1]!;
      const active = document.activeElement;
      const inside = active instanceof Node && container.contains(active);
      if (e.shiftKey && (active === first || !inside)) {
        e.preventDefault();
        last.focus();
      } else if (!e.shiftKey && (active === last || !inside)) {
        e.preventDefault();
        first.focus();
      }
    };
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('keydown', onKey);
      const at = trapStack.lastIndexOf(id);
      if (at !== -1) trapStack.splice(at, 1);
    };
  }, [containerRef, enabled]);
}
